import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Plus, CheckCircle, X, ArrowsClockwise, Trash, PencilSimple, HandCoins, Coins, Truck, MagnifyingGlass, Printer } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalInput } from "../components/Brutal";
import { PriceChips } from "../components/PriceChips";
import { openPackingSlip } from "../lib/packingSlip";
import { FixCreditDialog } from "../components/FixCreditDialog";
import PaidOutAuditDialog from "../components/PaidOutAuditDialog";
import { ShippingCostsCard } from "../components/AccountCharges";
import { useAuth } from "../AuthContext";

/**
 * Settlement Ledger — track e-transfers received directly from a distributor's
 * customer, assign the source distributor, compute the margin credit owed
 * back, then flag when the payout is sent.
 *
 * Read-only for partner + distributor roles. Owner/CFO can log, assign, mark
 * paid, unassign, delete.
 */
export default function SettlementsPage() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState({ items: [], counts: {}, totals: {} });
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(""); // "", "unassigned", "pending_payout", "paid_out"
  const [showAdd, setShowAdd] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [addItemTarget, setAddItemTarget] = useState(null);
  const [payoutTarget, setPayoutTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [fixCreditTarget, setFixCreditTarget] = useState(null);
  const [saleRequests, setSaleRequests] = useState([]);
  const [owed, setOwed] = useState(null); // v409.5 payouts-owed summary
  const [distFilter, setDistFilter] = useState(null); // {id, name} | null

  const { user, isSuperOwner } = useAuth();
  const canWrite = true; // backend enforces; UI will just show the buttons — 4xx surfaces as toast
  // v406.0 GOD MODE — owner/admin (and allowlisted super-owners) can edit any
  // settlement row in place; every edit is audit-trailed as "edited by …".
  const canGodEdit = ["owner", "admin"].includes(user?.role) || isSuperOwner;
  // v400.98: the root owner can delete ANY settlement, incl. paid-out
  // duplicates (god mode). Others keep the audit protection.
  const canDelete = (r) => canWrite && (r.status !== "paid_out" || user?.role === "owner");

  const fmt$ = (n) =>
    `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString(i18n.language === "ru" ? "ru-RU" : "en-US",
      { year: "numeric", month: "short", day: "numeric" }) : "—";

  const load = async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const [{ data }, sr, ow] = await Promise.all([
        api.get("/settlements", { params }),
        api.get("/sale-requests", { params: { status: "pending" } }).catch(() => ({ data: { items: [] } })),
        api.get("/settlements/payouts-owed").catch(() => ({ data: null })), // owner/cfo only
      ]);
      setData(data);
      setSaleRequests(sr.data.items || []);
      setOwed(ow.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const confirmSaleRequest = async (sr) => {
    const amt = window.prompt(t("sale_request.confirm_prompt", { code: sr.code }), sr.total_amount.toFixed(2));
    if (amt === null) return;
    const num = Number(amt);
    if (!Number.isFinite(num) || num < 0) {
      toast.error(t("sale_request.invalid_price"));
      return;
    }
    try {
      await api.post(`/sale-requests/${sr.id}/confirm`, { amount_received: num });
      toast.success(t("sale_request.confirmed_toast", { code: sr.code }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const cancelSaleRequest = async (sr) => {
    if (!window.confirm(t("sale_request.cancel_confirm", { code: sr.code }))) return;
    try {
      await api.post(`/sale-requests/${sr.id}/cancel`);
      toast.success(t("common.deleted"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  useEffect(() => {
    api.get("/distributors").then(({ data }) => setDistributors(data)).catch(() => {});
  }, []);

  const finalizeOpen = async (r) => {
    const rem = Number(r.remaining_balance || 0);
    const msg = r.master_sale
      ? t("settlements.finalize_confirm_master", { v: fmt$(r.gross_amount) })
      : rem > 0 ? t("settlements.finalize_confirm", { v: fmt$(rem) })
      : rem < 0 ? t("settlements.finalize_confirm_negative", { v: fmt$(Math.abs(rem)) })
      : t("settlements.finalize_confirm_zero");
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/settlements/${r.id}/finalize`, {});
      toast.success(t("settlements.finalize_success"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // v414.41 — $25 free-shipping deduction on ABL payouts (house shipped the
  // order, customer had free shipping ≥ $200). Manual confirm, reversible
  // while the payout is still pending.
  const toggleShipDeduction = async (r, apply) => {
    if (apply && !window.confirm(t("settlements.ship_deduct_confirm"))) return;
    try {
      await api.post(`/settlements/${r.id}/shipping-deduction`, { apply });
      toast.success(apply ? t("settlements.ship_deducted_success") : t("settlements.ship_deduct_undone"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = distFilter
    ? data.items.filter((r) => r.assigned_distributor_id === distFilter.id)
    : data.items;

  const statusPill = (s) => {
    const cls = {
      unassigned: "bg-amber-50 text-amber-700 border-amber-200",
      open: "bg-sky-50 text-sky-700 border-sky-200",
      pending_payout: "bg-[#fbf6e7] text-[#8a6a1c] border-[#c9a24a]",
      paid_out: "bg-emerald-50 text-emerald-700 border-emerald-200",
    }[s] || "bg-zinc-100 text-zinc-700 border-zinc-200";
    return (
      <span className={`text-[10px] font-medium px-1.5 py-0.5 border rounded-full ${cls}`} data-testid={`settlement-status-${s}`}>
        {t(`settlements.status_${s}`)}
      </span>
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="settlements-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="min-w-0">
          <Link to="/" className="inline-flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-zinc-800 mb-1">
            <ArrowLeft size={12} weight="bold" /> {t("common.back")}
          </Link>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{t("settlements.title")}</h1>
          <p className="text-sm text-zinc-600 mt-0.5">{t("settlements.subtitle")}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <BrutalButton variant="secondary" onClick={() => setShowAudit(true)} data-testid="settlements-audit-btn">
            <MagnifyingGlass size={14} weight="bold" /> {t("audit.open_btn")}
          </BrutalButton>
          <BrutalButton onClick={() => setShowAdd(true)} data-testid="settlements-add-btn">
            <Plus size={14} weight="bold" /> {t("settlements.log_new")}
          </BrutalButton>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {["unassigned", "open", "pending_payout", "paid_out"].map((key) => (
          <button
            key={key}
            onClick={() => setFilter(filter === key ? "" : key)}
            className={`text-left border rounded-xl p-4 transition ${
              filter === key ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 bg-white hover:bg-zinc-50"
            }`}
            data-testid={`settlements-tile-${key}`}
          >
            <div className="text-[10px] uppercase tracking-wide font-semibold text-zinc-500">
              {t(`settlements.status_${key}`)}
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-xl font-semibold tabular-nums">{fmt$(data.totals?.[key] || 0)}</span>
              <span className="text-xs text-zinc-500">
                {t("settlements.count", { n: data.counts?.[key] || 0 })}
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* v409.5 — Payouts Owed roll-up (owner/cfo) */}
      {owed && owed.rows?.length > 0 && (
        <section className="bg-white border-2 border-emerald-300 rounded-xl p-4" data-testid="payouts-owed-card">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <h2 className="font-semibold text-base flex items-center gap-2">
                <HandCoins size={18} weight="bold" className="text-emerald-700" />
                {t("settlements.owed_title")}
              </h2>
              <p className="text-xs text-zinc-500 mt-0.5">{t("settlements.owed_hint")}</p>
            </div>
            <div className="text-right">
              <span className={`text-xl font-bold tabular-nums ${owed.total_net >= 0 ? "text-emerald-700" : "text-rose-600"}`} data-testid="payouts-owed-total">
                {fmt$(owed.total_net)}
              </span>
              <p className="text-[11px] text-zinc-500">
                {t("settlements.owed_total_sub", { n: owed.rows.length })}
                {owed.total_charges > 0 && <> · {t("settlements.owed_charges_sub", { v: fmt$(owed.total_charges) })}</>}
                {owed.total_paid > 0 && <> · {t("settlements.owed_paid_total_sub", { v: fmt$(owed.total_paid) })}</>}
              </p>
            </div>
          </div>
          <div className="mt-3 space-y-1.5">
            {owed.rows.map((r) => (
              <button
                key={r.id}
                onClick={() => { setDistFilter({ id: r.id, name: r.name }); setFilter(r.count > 0 ? "pending_payout" : "paid_out"); }}
                className={`w-full flex flex-wrap items-center gap-x-3 gap-y-1 text-left rounded-lg border px-3 py-2 transition hover:bg-emerald-50 ${
                  distFilter?.id === r.id ? "border-emerald-400 bg-emerald-50" : "border-zinc-200"
                }`}
                data-testid={`payouts-owed-row-${r.id}`}
              >
                <span className="font-medium text-sm">{r.name}</span>
                <span className="text-[11px] text-zinc-500">
                  {r.count > 0 && <>{t("settlements.owed_count", { n: r.count })}{r.oldest && <> · {t("settlements.owed_oldest", { d: r.oldest })}</>}</>}
                  {r.paid_count > 0 && (
                    <span data-testid={`payouts-paid-life-${r.id}`}>
                      {r.count > 0 && " · "}
                      {t("settlements.owed_paid_life", { v: fmt$(r.paid_total), n: r.paid_count })}
                      {r.last_paid && <> ({r.last_paid})</>}
                    </span>
                  )}
                </span>
                <span className="ml-auto text-xs text-zinc-500 tabular-nums">
                  {r.count > 0 && <>{fmt$(r.gross_owed)}{r.charges > 0 && <span className="text-rose-600"> − {fmt$(r.charges)}</span>}</>}
                </span>
                <span className={`font-bold tabular-nums ${r.net_owed > 0 ? "text-emerald-700" : r.net_owed < 0 ? "text-rose-600" : "text-zinc-400"}`} data-testid={`payouts-owed-net-${r.id}`}>
                  {fmt$(r.net_owed)}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {distFilter && (
        <div className="flex items-center gap-2 text-sm" data-testid="dist-filter-chip">
          <span className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-300 text-emerald-800 rounded-full px-3 py-1 font-medium">
            {t("settlements.owed_filtering", { name: distFilter.name })}
            <button onClick={() => setDistFilter(null)} className="hover:text-emerald-950" data-testid="dist-filter-clear">
              <X size={13} weight="bold" />
            </button>
          </span>
        </div>
      )}

      {/* v407.1 — running shipping-cost tally (hidden until a shipping charge exists) */}
      <ShippingCostsCard />

      {/* v401.1 Pending Sale Requests — match the e-transfer memo code */}
      {saleRequests.length > 0 && (
        <section className="bg-white border-2 border-[#c9a24a] rounded-xl p-4" data-testid="pending-sale-requests">
          <h2 className="font-semibold text-base">{t("sale_request.ledger_title")}</h2>
          <p className="text-xs text-zinc-500 mt-0.5">{t("sale_request.ledger_hint")}</p>
          <div className="mt-3 space-y-2">
            {saleRequests.map((sr) => (
              <div key={sr.id} className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-zinc-200 p-3" data-testid={`sale-request-row-${sr.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-bold text-base bg-[#fbf6e7] border border-[#c9a24a]/60 rounded-md px-2 py-0.5" data-testid={`sr-code-${sr.id}`}>{sr.code}</span>
                    <span className="text-sm font-medium">{sr.requester_name}</span>
                    <span className="text-xs text-zinc-400">{fmtDate(sr.created_at)}</span>
                  </div>
                  <p className="text-xs text-zinc-600 mt-1">
                    {sr.items.map((it) => `${it.quantity}x ${it.product_name}`).join(" · ")}
                    {sr.customer_name ? ` — ${sr.customer_name}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-semibold tabular-nums">{fmt$(sr.total_amount)}</span>
                  {sr.expected_payout != null && sr.expected_payout !== 0 && (
                    <span
                      className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 whitespace-nowrap"
                      data-testid={`sr-payout-${sr.id}`}
                    >
                      {t("sale_request.payout_label", { amount: fmt$(sr.expected_payout) })}
                    </span>
                  )}
                  {canWrite && (
                    <>
                      <button
                        onClick={() => openPackingSlip(sr)}
                        className="text-zinc-400 hover:text-zinc-900 p-1"
                        title={t("sale_request.packing_slip")}
                        data-testid={`sr-slip-${sr.id}`}
                      >
                        <Printer size={15} weight="bold" />
                      </button>
                      <BrutalButton size="sm" onClick={() => confirmSaleRequest(sr)} data-testid={`sr-confirm-${sr.id}`}>
                        <CheckCircle size={14} weight="bold" /> {t("sale_request.confirm_btn")}
                      </BrutalButton>
                      <button
                        onClick={() => cancelSaleRequest(sr)}
                        className="text-zinc-400 hover:text-red-600 p-1"
                        title={t("common.cancel")}
                        data-testid={`sr-cancel-${sr.id}`}
                      >
                        <X size={14} weight="bold" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Table / list */}
      {loading ? (
        <div className="text-sm text-zinc-500 bg-zinc-50 border border-zinc-100 rounded-md p-4">{t("common.loading")}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 text-center" data-testid="settlements-empty">
          <p className="text-sm font-medium text-zinc-900">{t("settlements.empty")}</p>
          <p className="text-xs text-zinc-500 mt-1">{t("settlements.empty_hint")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => (
            <div key={r.id} data-testid={`settlement-${r.id}`}
              className="bg-white border border-zinc-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-semibold text-zinc-900 truncate">{r.received_from}</span>
                  {(r.entry_type === "return" || r.source === "return") && (
                    <span
                      className="text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-zinc-950 border-zinc-950 text-[#e4cf94]"
                      data-testid={`settlement-return-pill-${r.id}`}
                    >
                      {t("settlements.type_return")}
                    </span>
                  )}
                  {statusPill(r.status)}
                  {r.source === "abl_order" && r.free_ship_eligible && (
                    r.shipping_deducted ? (
                      <span
                        className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full border bg-rose-50 text-rose-700 border-rose-200 inline-flex items-center gap-1"
                        title={t("settlements.ship_deducted_pill_title")}
                        data-testid={`settlement-shipdeducted-pill-${r.id}`}
                      >
                        <Truck size={10} weight="bold" /> {t("settlements.ship_deducted_pill")}
                      </span>
                    ) : (
                      <span
                        className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-300 inline-flex items-center gap-1"
                        title={t("settlements.ship_free_pill_title")}
                        data-testid={`settlement-freeship-pill-${r.id}`}
                      >
                        <Truck size={10} weight="bold" /> {t("settlements.ship_free_pill")}
                      </span>
                    )
                  )}
                  {r.edited && (
                    <span
                      className="text-[10px] font-medium px-1.5 py-0.5 rounded-full border bg-violet-50 text-violet-700 border-violet-200 inline-flex items-center gap-1"
                      title={t("settlements.edited_by", { who: r.edited_by_name || r.edited_by_email || "", when: fmtDate(r.edited_at) })}
                      data-testid={`settlement-edited-badge-${r.id}`}
                    >
                      <PencilSimple size={10} weight="bold" /> {t("settlements.edited_badge")}
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {fmtDate(r.received_at)}
                  {r.reference && <> · {t("settlements.ref")}: {r.reference}</>}
                  {r.created_by_name && (
                    <> · <span data-testid={`settlement-logged-by-${r.id}`}>{t("settlements.logged_by", { name: r.created_by_name })}</span></>
                  )}
                  {r.paid_out_by_name && (
                    <> · <span data-testid={`settlement-paid-by-${r.id}`}>{t("settlements.paid_out_by", { name: r.paid_out_by_name })}</span></>
                  )}
                  {r.return_wholesale_value != null && (
                    <> · <span className="font-semibold text-[#8a6a1c]">{t("settlements.return_ws_value", { v: fmt$(r.return_wholesale_value) })}</span></>
                  )}
                </div>
                {r.assigned_distributor_name && (
                  <div className="text-xs text-zinc-700 mt-1">
                    → <span className="font-medium">
                      {r.master_sale ? t("settlements.master_label") : r.assigned_distributor_name}
                    </span>
                    {r.distributor_credit != null && r.wholesale_basis != null && !r.master_sale && (
                      <> · {t("settlements.basis")} {fmt$(r.wholesale_basis)} · <span className={r.distributor_credit >= 0 ? "text-emerald-700 font-semibold" : "text-rose-700 font-semibold"}>{t("settlements.credit")} {fmt$(r.distributor_credit)}</span></>
                    )}
                  </div>
                )}
                {(r.items || []).length > 0 && (
                  <div className="text-xs text-zinc-600 mt-1" data-testid={`settlement-items-${r.id}`}>
                    {r.items.map((it) => `${it.quantity}x ${it.product_name} (${fmt$(it.line_basis)})`).join(" · ")}
                  </div>
                )}
                {r.status === "open" && (
                  <div className="text-xs mt-1" data-testid={`settlement-open-remaining-${r.id}`}>
                    <span className={`font-semibold ${Number(r.remaining_balance) >= 0 ? "text-sky-700" : "text-rose-700"}`}>
                      {t("settlements.remaining")}: {fmt$(r.remaining_balance)}
                    </span>
                    {" · "}
                    <span className="text-zinc-500">{t("settlements.allocated")}: {fmt$(r.allocated_basis)}</span>
                  </div>
                )}
                {r.status === "paid_out" && Number(r.deductions_total) > 0 && (
                  <div className="text-xs mt-1" data-testid={`settlement-net-paid-${r.id}`}>
                    <span className="font-semibold text-emerald-700">
                      {t("settlements.payout_net_paid", { net: fmt$(r.net_payout) })}
                    </span>
                    {" · "}
                    <span className="text-rose-700" title={(r.deductions || []).map((d) => `$${Number(d.amount).toFixed(2)} ${d.reason}`).join(" · ")}>
                      {t("settlements.payout_deducted", { amount: fmt$(r.deductions_total) })}
                    </span>
                  </div>
                )}
                {r.note && <div className="text-xs italic text-zinc-500 mt-1 truncate" title={r.note}>&ldquo;{r.note}&rdquo;</div>}
              </div>
              <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                <div className="text-lg font-semibold tabular-nums text-zinc-900 mr-1">{fmt$(r.gross_amount)}</div>
                {r.status === "unassigned" && (
                  <BrutalButton size="sm" onClick={() => setAssignTarget(r)} data-testid={`settlement-assign-${r.id}`}>
                    {t("settlements.assign")}
                  </BrutalButton>
                )}
                {r.status === "open" && (
                  <>
                    <BrutalButton size="sm" onClick={() => setAddItemTarget(r)} data-testid={`settlement-add-item-${r.id}`}>
                      <Plus size={12} weight="bold" /> {t("settlements.add_item")}
                    </BrutalButton>
                    <BrutalButton size="sm" variant="secondary" onClick={() => finalizeOpen(r)} data-testid={`settlement-finalize-${r.id}`}>
                      <CheckCircle size={12} weight="bold" /> {t("settlements.finalize")}
                    </BrutalButton>
                    <BrutalButton size="sm" variant="secondary" onClick={async () => {
                      try { await api.post(`/settlements/${r.id}/unassign`, {}); toast.success(t("settlements.unassigned_success")); load(); }
                      catch (e) { toast.error(formatApiError(e)); }
                    }} data-testid={`settlement-unassign-${r.id}`} title={t("settlements.unassign")}>
                      <ArrowsClockwise size={12} weight="bold" />
                    </BrutalButton>
                  </>
                )}
                {r.status === "pending_payout" && (
                  <>
                    {r.source === "abl_order" && r.free_ship_eligible && (
                      r.shipping_deducted ? (
                        <BrutalButton size="sm" variant="secondary"
                          onClick={() => toggleShipDeduction(r, false)}
                          data-testid={`settlement-shipdeduct-undo-${r.id}`}
                          title={t("settlements.ship_deduct_undo")}
                          className="!border-rose-300 !text-rose-700 hover:!bg-rose-50">
                          <Truck size={12} weight="bold" /> {t("settlements.ship_deduct_undo_btn")}
                        </BrutalButton>
                      ) : (
                        <BrutalButton size="sm" variant="secondary"
                          onClick={() => toggleShipDeduction(r, true)}
                          data-testid={`settlement-shipdeduct-${r.id}`}
                          title={t("settlements.ship_deduct_title")}
                          className="!border-amber-400 !text-amber-800 hover:!bg-amber-50">
                          <Truck size={12} weight="bold" /> {t("settlements.ship_deduct_btn")}
                        </BrutalButton>
                      )
                    )}
                    <BrutalButton size="sm" variant="secondary" onClick={() => setPayoutTarget(r)}
                      data-testid={`settlement-pay-${r.id}`}>
                      <CheckCircle size={12} weight="bold" /> {t("settlements.mark_paid_out")}
                    </BrutalButton>
                    <BrutalButton size="sm" variant="secondary" onClick={async () => {
                      try { await api.post(`/settlements/${r.id}/unassign`, {}); toast.success(t("settlements.unassigned_success")); load(); }
                      catch (e) { toast.error(formatApiError(e)); }
                    }} data-testid={`settlement-unassign-${r.id}`} title={t("settlements.unassign")}>
                      <ArrowsClockwise size={12} weight="bold" />
                    </BrutalButton>
                  </>
                )}
                {canGodEdit && (r.status === "pending_payout" || r.status === "paid_out")
                  && (r.items || []).length > 0 && !r.master_sale && !(Number(r.deductions_total) > 0) && (
                  <BrutalButton size="sm" variant="secondary" onClick={() => setFixCreditTarget(r)}
                    data-testid={`settlement-fix-credit-${r.id}`} title="Fix credit (correct item basis)">
                    <Coins size={12} weight="bold" />
                  </BrutalButton>
                )}
                {canGodEdit && (
                  <BrutalButton size="sm" variant="secondary" onClick={() => setEditTarget(r)}
                    data-testid={`settlement-edit-${r.id}`} title={t("settlements.edit")}>
                    <PencilSimple size={12} weight="bold" />
                  </BrutalButton>
                )}
                {canDelete(r) && (
                  <BrutalButton size="sm" variant="secondary" onClick={async () => {
                    if (!window.confirm(t("settlements.delete_confirm"))) return;
                    try { await api.delete(`/settlements/${r.id}`); toast.success(t("common.deleted")); load(); }
                    catch (e) { toast.error(formatApiError(e)); }
                  }} data-testid={`settlement-delete-${r.id}`}
                    className="!border-rose-300 !text-rose-700 hover:!bg-rose-50 !px-2">
                    <Trash size={12} weight="bold" />
                  </BrutalButton>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showAudit && <PaidOutAuditDialog onClose={() => setShowAudit(false)} />}
      {showAdd && (
        <AddSettlementDialog
          distributors={distributors}
          saleRequests={saleRequests}
          onClose={() => setShowAdd(false)}
          onCreated={(doc, matchedId) => {
            setShowAdd(false);
            load();
            if (doc && matchedId) setAssignTarget({ ...doc, _preselectId: matchedId });
          }}
          t={t}
        />
      )}
      {assignTarget && (
        <AssignSettlementDialog
          settlement={assignTarget}
          distributors={distributors}
          onClose={() => setAssignTarget(null)}
          onAssigned={() => { setAssignTarget(null); load(); }}
          t={t}
        />
      )}
      {fixCreditTarget && (
        <FixCreditDialog
          settlement={fixCreditTarget}
          onClose={() => setFixCreditTarget(null)}
          onFixed={() => { setFixCreditTarget(null); load(); }}
        />
      )}
      {addItemTarget && (
        <AddItemDialog
          settlement={addItemTarget}
          onClose={() => setAddItemTarget(null)}
          onAdded={() => { setAddItemTarget(null); load(); }}
          t={t}
        />
      )}
      {payoutTarget && (
        <PayoutDialog
          settlement={payoutTarget}
          onClose={() => setPayoutTarget(null)}
          onPaid={() => { setPayoutTarget(null); load(); }}
          t={t}
        />
      )}
      {editTarget && (
        <EditSettlementDialog
          settlement={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(); }}
          t={t}
        />
      )}
    </div>
  );
}

function EditSettlementDialog({ settlement, onClose, onSaved, t }) {
  const grossLocked = settlement.status === "paid_out";
  const [form, setForm] = useState({
    gross_amount: String(settlement.gross_amount ?? ""),
    received_from: settlement.received_from || "",
    received_at: (settlement.received_at || "").slice(0, 10),
    reference: settlement.reference || "",
    note: settlement.note || "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        received_from: form.received_from.trim() || null,
        received_at: form.received_at || null,
        reference: form.reference,
        note: form.note,
      };
      if (!grossLocked && form.gross_amount !== "") payload.gross_amount = Number(form.gross_amount);
      await api.patch(`/settlements/${settlement.id}`, payload);
      toast.success(t("common.saved"));
      onSaved();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="settlement-edit-dialog"
        className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 space-y-3 max-h-[90vh] overflow-y-auto">
        <h2 className="font-semibold text-lg">{t("settlements.edit_title")}</h2>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-zinc-600">{t("settlements.edit_gross")}</label>
            <BrutalInput type="number" step="0.01" min="0" value={form.gross_amount} disabled={grossLocked}
              onChange={(e) => setForm({ ...form, gross_amount: e.target.value })} data-testid="settlement-edit-gross" />
            {grossLocked && <p className="text-[10px] text-zinc-400 mt-0.5">{t("settlements.gross_locked_hint")}</p>}
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600">{t("settlements.edit_date")}</label>
            <BrutalInput type="date" value={form.received_at}
              onChange={(e) => setForm({ ...form, received_at: e.target.value })} data-testid="settlement-edit-date" />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-zinc-600">{t("settlements.edit_from")}</label>
          <BrutalInput value={form.received_from}
            onChange={(e) => setForm({ ...form, received_from: e.target.value })} data-testid="settlement-edit-from" />
        </div>
        <div>
          <label className="text-xs font-medium text-zinc-600">{t("settlements.edit_ref")}</label>
          <BrutalInput value={form.reference}
            onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="settlement-edit-ref" />
        </div>
        <div>
          <label className="text-xs font-medium text-zinc-600">{t("settlements.edit_note")}</label>
          <BrutalInput value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="settlement-edit-note" />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <BrutalButton type="button" variant="secondary" onClick={onClose} data-testid="settlement-edit-cancel">
            {t("common.cancel")}
          </BrutalButton>
          <BrutalButton type="submit" disabled={busy} data-testid="settlement-edit-save">
            {busy ? t("common.saving") : t("common.save")}
          </BrutalButton>
        </div>
      </form>
    </div>
  );
}

function AddSettlementDialog({ distributors = [], saleRequests = [], onClose, onCreated, t }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ gross_amount: "", received_at: today, received_from: "", reference: "", note: "" });
  const [busy, setBusy] = useState(false);
  // v407.0 Personal use payment — settle OWED family/sample entries in one step.
  const [personalUse, setPersonalUse] = useState(false);
  const [owedSummary, setOwedSummary] = useState(null);

  useEffect(() => {
    if (!personalUse) return;
    api
      .get("/settlements/personal-owed")
      .then(({ data }) => {
        setOwedSummary(data);
        setForm((f) => ({
          ...f,
          gross_amount: f.gross_amount === "" && data.total > 0 ? String(data.total) : f.gross_amount,
          received_from: f.received_from.trim() === "" ? t("settlements.personal_from_default") : f.received_from,
        }));
      })
      .catch((e) => toast.error(formatApiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalUse]);

  // v401.2 sale-request auto-match: if the memo/reference contains a PENDING
  // sale request code (normalized: "hp01 6130" matches HP01-6130), offer a
  // one-tap confirm — no duplicate settlement gets logged.
  const norm = (s) => (s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  const srMatch = useMemo(() => {
    const text = norm(`${form.received_from} ${form.reference} ${form.note}`);
    if (!text) return null;
    return saleRequests.find((sr) => text.includes(norm(sr.code))) || null;
  }, [form.received_from, form.reference, form.note, saleRequests]);

  const confirmMatched = async () => {
    setBusy(true);
    try {
      const amt = Number(form.gross_amount);
      await api.post(`/sale-requests/${srMatch.id}/confirm`, {
        amount_received: Number.isFinite(amt) && amt > 0 ? amt : undefined,
        note: form.note.trim() || undefined,
      });
      toast.success(t("sale_request.confirmed_toast", { code: srMatch.code }));
      onCreated(null, null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Reconciliation-code auto-match: customers quote codes like "RV-01" in
  // e-transfers; detect one in the from/reference text and offer to assign.
  const matched = useMemo(() => {
    const text = `${form.received_from} ${form.reference}`.toUpperCase();
    if (!text.trim()) return null;
    const candidates = distributors
      .filter((d) => d.distributor_code)
      .sort((a, b) => b.distributor_code.length - a.distributor_code.length);
    return candidates.find((d) => text.includes(d.distributor_code.toUpperCase())) || null;
  }, [form.received_from, form.reference, distributors]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.received_from.trim() || !Number(form.gross_amount)) {
      toast.error(t("settlements.form_invalid"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/settlements", {
        gross_amount: Number(form.gross_amount),
        received_at: form.received_at,
        received_from: form.received_from.trim(),
        reference: form.reference.trim() || null,
        note: form.note.trim() || null,
        personal_use: personalUse,
      });
      toast.success(t("settlements.created_success"));
      // v407.0 Personal use payment — surface what got settled.
      if (data.personal_applied) {
        const pa = data.personal_applied;
        if (pa.count > 0) {
          toast.success(
            t("settlements.personal_applied_toast", {
              count: pa.count,
              amount: pa.applied_total.toFixed(2),
            }),
            { duration: 8000 }
          );
        } else {
          toast.warning(t("settlements.personal_none_applied_toast"), { duration: 8000 });
        }
        if (pa.remainder > 0 && pa.count > 0) {
          toast.warning(
            t("settlements.personal_remainder_toast", { amount: pa.remainder.toFixed(2) }),
            { duration: 10000 }
          );
        }
      }
      // v405.17 Auto Payment Match — surface the linked store order.
      if (data.matched_order) {
        toast.success(
          t("settlements.order_matched", {
            ref: data.matched_order.reference,
            name: data.matched_order.distributor_name,
          }),
          { duration: 8000 }
        );
        if (data.matched_order.amount_mismatch) {
          toast.warning(
            t("settlements.order_amount_mismatch", {
              total: data.matched_order.total,
            }),
            { duration: 10000 }
          );
        }
      }
      onCreated(data, matched?.id || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="settlement-add-dialog"
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md space-y-3">
        <h2 className="text-xl font-semibold tracking-tight">{t("settlements.log_new")}</h2>
        <div>
          <label className="block text-xs font-semibold mb-1">{t("settlements.received_from")}</label>
          <BrutalInput value={form.received_from} onChange={(e) => setForm({ ...form, received_from: e.target.value })}
            placeholder={t("settlements.received_from_placeholder")} data-testid="settlement-from-input" autoFocus />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold mb-1">{t("settlements.gross_amount")}</label>
            <BrutalInput type="number" step="0.01" min="0" value={form.gross_amount}
              onChange={(e) => setForm({ ...form, gross_amount: e.target.value })} data-testid="settlement-gross-input" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">{t("settlements.received_at")}</label>
            <BrutalInput type="date" value={form.received_at}
              onChange={(e) => setForm({ ...form, received_at: e.target.value })} data-testid="settlement-date-input" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1">{t("settlements.reference")}</label>
          <BrutalInput value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}
            placeholder={t("settlements.reference_placeholder")} data-testid="settlement-ref-input" />
        </div>
        {/* v407.0 Personal use payment — settles OWED family/sample entries */}
        <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-2">
          <label className="flex items-center gap-2 text-sm select-none cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 accent-amber-600"
              checked={personalUse}
              onChange={(e) => setPersonalUse(e.target.checked)}
              data-testid="settlement-personal-use-toggle"
            />
            <span className="font-semibold text-amber-900">{t("settlements.personal_use_label")}</span>
          </label>
          <p className="text-[11px] text-amber-800">{t("settlements.personal_use_hint")}</p>
          {personalUse && owedSummary && (
            owedSummary.count > 0 ? (
              <div
                className="rounded-md bg-white border border-amber-300 px-3 py-2 text-sm"
                data-testid="personal-owed-preview"
              >
                <p className="font-semibold text-amber-900">
                  {t("settlements.personal_owed_preview", {
                    count: owedSummary.count,
                    total: owedSummary.total.toFixed(2),
                  })}
                </p>
                <p className="text-xs text-zinc-600 mt-0.5">
                  {owedSummary.items
                    .slice(0, 4)
                    .map((it) => `${it.quantity}× ${it.product_name} ($${it.amount.toFixed(2)})`)
                    .join(" · ")}
                  {owedSummary.count > 4 ? ` · +${owedSummary.count - 4}` : ""}
                </p>
              </div>
            ) : (
              <p className="text-xs font-medium text-amber-800" data-testid="personal-owed-empty">
                {t("settlements.personal_no_owed")}
              </p>
            )
          )}
        </div>
        {!personalUse && srMatch && (
          <div
            className="rounded-lg border-2 border-[#c9a24a] bg-[#fbf6e7] px-3 py-2.5 text-sm"
            data-testid="sr-match-banner"
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono font-bold bg-white border border-[#c9a24a]/60 rounded px-1.5 py-0.5">{srMatch.code}</span>
              <span className="font-medium text-[#8a6a1c]">
                {t("settlements.sr_match_found", { name: srMatch.requester_name })}
              </span>
            </div>
            <p className="text-xs text-zinc-600 mt-1">
              {srMatch.items.map((it) => `${it.quantity}x ${it.product_name}`).join(" · ")} — ${srMatch.total_amount.toFixed(2)}
            </p>
            <BrutalButton
              type="button"
              size="sm"
              className="mt-2 w-full"
              onClick={confirmMatched}
              disabled={busy}
              data-testid="sr-match-confirm"
            >
              {busy ? t("common.saving") : t("settlements.sr_match_confirm_btn")}
            </BrutalButton>
          </div>
        )}
        {!personalUse && !srMatch && matched && (
          <div
            className="flex items-center gap-2 bg-emerald-50 border border-emerald-300 rounded-lg px-3 py-2 text-sm text-emerald-900"
            data-testid="code-match-chip"
          >
            <span className="text-[11px] font-bold px-1.5 py-0.5 bg-white border border-emerald-400 rounded font-mono tracking-wider">
              {matched.distributor_code}
            </span>
            <span className="font-medium">
              {t("settlements.code_match", { name: matched.name })}
            </span>
          </div>
        )}
        <div>
          <label className="block text-xs font-semibold mb-1">{t("settlements.note")}</label>
          <textarea rows={2} className="input-brutal w-full" value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="settlement-note-input" />
        </div>
        <div className="flex gap-3 pt-2">
          <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </BrutalButton>
          <BrutalButton type="submit" className="flex-1" disabled={busy} data-testid="settlement-add-submit">
            {busy ? t("common.saving") : t("common.save")}
          </BrutalButton>
        </div>
      </form>
    </div>
  );
}

function AssignSettlementDialog({ settlement, distributors, onClose, onAssigned, t }) {
  const gross = Number(settlement.gross_amount);
  const [distId, setDistId] = useState(settlement._preselectId || "");
  const [pricing, setPricing] = useState([]);
  const [items, setItems] = useState([]); // [{product_id, quantity, unit_basis}]
  const [manualMode, setManualMode] = useState(false);
  const [manualBasis, setManualBasis] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const fmt$ = (n) => `$${Number(n || 0).toFixed(2)}`;

  const isMaster = distId === "master";

  useEffect(() => {
    setPricing([]);
    setItems([]);
    setManualMode(false);
    if (!distId) return;
    const req = distId === "master"
      ? api.get("/products").then(({ data }) =>
          (data || [])
            .filter((p) => (p.suggested_retail_price ?? p.wholesale_price) != null)
            .map((p) => ({
              product_id: p.id,
              name: p.name,
              their_wholesale: p.suggested_retail_price ?? p.wholesale_price,
              wholesale_price: p.wholesale_price,
              suggested_retail_price: p.suggested_retail_price,
              current_stock: p.current_stock,
            })))
      : api.get(`/distributors/${distId}/pricing`).then(({ data }) =>
          (data || []).filter((r) => (r.their_wholesale ?? r.base_wholesale) != null));
    req
      .then((rows) => {
        setPricing(rows);
        if (rows.length > 0) setItems([{ product_id: "", quantity: 1, unit_basis: "" }]);
      })
      .catch(() => {});
  }, [distId]);

  const usingItems = pricing.length > 0 && !manualMode;
  const rowFor = (pid) => pricing.find((x) => x.product_id === pid);
  const wsOf = (r) => r?.wholesale_price ?? r?.their_wholesale ?? r?.base_wholesale;
  const unitFor = (pid) => {
    const r = rowFor(pid);
    return r ? Number(r.their_wholesale ?? r.base_wholesale) : null;
  };
  const setItem = (idx, patch) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const validItems = items.filter((it) => it.product_id && Number(it.quantity) >= 1);
  const distName = (distributors.find((d) => d.id === distId) || {}).name || t("settlements.the_distributor", { defaultValue: "the distributor" });
  const allocated = +validItems
    .reduce((s, it) => s + (Number(it.unit_basis) || 0) * Number(it.quantity), 0)
    .toFixed(2);
  const remaining = +(gross - allocated).toFixed(2);
  const manualCredit = manualBasis === "" ? null : +(gross - Number(manualBasis)).toFixed(2);

  const submit = async (finalize) => {
    if (!distId) {
      toast.error(t("settlements.form_invalid"));
      return;
    }
    if (usingItems) {
      if (validItems.length === 0) {
        toast.error(t("settlements.no_items_yet"));
        return;
      }
      if (finalize) {
        const msg = isMaster
          ? t("settlements.finalize_confirm_master", { v: fmt$(gross) })
          : remaining > 0 ? t("settlements.finalize_confirm", { v: fmt$(remaining) })
          : remaining < 0 ? t("settlements.finalize_confirm_negative", { v: fmt$(Math.abs(remaining)) })
          : t("settlements.finalize_confirm_zero");
        if (!window.confirm(msg)) return;
      }
    } else if (manualBasis === "") {
      toast.error(t("settlements.form_invalid"));
      return;
    }
    setBusy(true);
    try {
      if (usingItems) {
        await api.post(`/settlements/${settlement.id}/assign`, {
          distributor_id: distId,
          items: validItems.map((it) => ({
            product_id: it.product_id,
            quantity: Math.max(1, Number(it.quantity) || 1),
            unit_basis: it.unit_basis === "" ? null : Number(it.unit_basis),
          })),
          finalize,
          note: note.trim() || null,
        });
        toast.success(finalize ? t("settlements.finalize_success") : t("settlements.assigned_open_success"));
      } else {
        await api.post(`/settlements/${settlement.id}/assign`, {
          distributor_id: distId,
          wholesale_basis: Number(manualBasis),
          note: note.trim() || null,
        });
        toast.success(t("settlements.assigned_success"));
      }
      onAssigned();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="settlement-assign-dialog"
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-lg space-y-3 my-8">
        <h2 className="text-xl font-semibold tracking-tight">{t("settlements.assign_title")}</h2>
        <p className="text-sm text-zinc-600">
          {t("settlements.assign_subtitle", {
            from: settlement.received_from,
            amount: fmt$(gross),
          })}
        </p>
        <div>
          <label className="block text-xs font-semibold mb-1">{t("settlements.distributor")}</label>
          <select className="input-brutal w-full" value={distId} onChange={(e) => setDistId(e.target.value)}
            data-testid="settlement-assign-distributor-select" required>
            <option value="">— {t("settlements.pick_distributor")} —</option>
            <option value="master">{t("settlements.master_option")}</option>
            {distributors.map((d) => (
              <option key={d.id} value={d.id}>{d.name} · {d.email}</option>
            ))}
          </select>
        </div>

        {distId && usingItems && (
          <div className="rounded-lg border border-[#c9a24a] bg-[#fbf6e7]/60 p-3 space-y-2" data-testid="settlement-line-items">
            <label className="block text-xs font-semibold text-[#8a6a1c]">{t("settlements.line_items")}</label>
            {items.map((it, idx) => (
              <div key={idx} data-testid={`settlement-item-row-${idx}`}>
                <div className="grid grid-cols-[1fr_3.5rem_5rem_auto] gap-2 items-center">
                  <select
                    className="input-brutal w-full min-w-0"
                    value={it.product_id}
                    onChange={(e) => {
                      const pid = e.target.value;
                      const u = unitFor(pid);
                      setItem(idx, { product_id: pid, unit_basis: u != null ? u.toFixed(2) : "" });
                    }}
                    data-testid={`settlement-item-product-${idx}`}
                  >
                    <option value="">— {t("settlements.pick_product")} —</option>
                    {pricing.map((r) => (
                      <option key={r.product_id} value={r.product_id}>
                        {r.name} · ${Number(r.their_wholesale ?? r.base_wholesale).toFixed(2)} · {t("settlements.held_n", { n: r.current_stock })}
                      </option>
                    ))}
                  </select>
                  <input type="number" min="1" className="input-brutal w-full" value={it.quantity}
                    onChange={(e) => setItem(idx, { quantity: e.target.value })}
                    title={t("settlements.qty")} data-testid={`settlement-item-qty-${idx}`} />
                  <input type="number" step="0.01" min="0" className="input-brutal w-full" value={it.unit_basis}
                    onChange={(e) => setItem(idx, { unit_basis: e.target.value })}
                    title={t("settlements.unit_basis")} data-testid={`settlement-item-basis-${idx}`} />
                  <button type="button" onClick={() => removeItem(idx)}
                    className="text-zinc-400 hover:text-red-600 p-1" data-testid={`settlement-item-remove-${idx}`}>
                    <X size={14} weight="bold" />
                  </button>
                </div>
                {it.product_id && (
                  <PriceChips
                    wholesale={wsOf(rowFor(it.product_id))}
                    retail={rowFor(it.product_id)?.suggested_retail_price}
                    value={it.unit_basis}
                    onPick={(v) => setItem(idx, { unit_basis: v })}
                    testIdPrefix={`settlement-item-chip-${idx}`}
                  />
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => setItems((prev) => [...prev, { product_id: "", quantity: 1, unit_basis: "" }])}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#8a6a1c] hover:underline"
              data-testid="settlement-add-another-item"
            >
              <Plus size={12} weight="bold" /> {t("settlements.add_another_item")}
            </button>
          </div>
        )}

        {distId && usingItems && (
          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 text-sm space-y-1" data-testid="settlement-running-balance">
            <div className="flex justify-between">
              <span className="text-xs text-zinc-600">{t("settlements.gross")}</span>
              <span className="tabular-nums font-medium">{fmt$(gross)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-zinc-600">{t("settlements.allocated")}</span>
              <span className="tabular-nums font-medium">{fmt$(allocated)}</span>
            </div>
            <div className="flex justify-between border-t border-zinc-200 pt-1">
              <span className="text-xs font-semibold text-zinc-700">{t("settlements.remaining")}</span>
              <span
                className={`text-base font-semibold tabular-nums ${remaining === 0 ? "text-emerald-700" : remaining > 0 ? "text-amber-700" : "text-rose-700"}`}
                data-testid="settlement-remaining-balance"
              >
                {fmt$(remaining)}
              </span>
            </div>
            {!isMaster && validItems.length > 0 && remaining > 0 && (
              <p className="text-[11px] text-zinc-500 pt-0.5" data-testid="credit-split-preview">
                {t("settlements.credit_split", {
                  house: fmt$(allocated), name: distName, owed: fmt$(remaining),
                  defaultValue: "House keeps {{house}} · {{name}} is owed {{owed}}",
                })}
              </p>
            )}
            {!isMaster && validItems.length > 0 && remaining <= 0 && (
              <div className="bg-red-50 border-2 border-red-300 rounded-md p-2 text-[11px] font-semibold text-red-700" data-testid="zero-credit-warning">
                {t("settlements.zero_credit_warning", {
                  name: distName,
                  defaultValue: "⚠ {{name}} gets $0.00 — the item prices swallow the whole transfer. Enter their WHOLESALE cost per line (the leftover becomes their credit). Only continue if they truly earn nothing on this sale.",
                })}
              </div>
            )}
          </div>
        )}

        {distId && pricing.length > 0 && !isMaster && (
          <button
            type="button"
            onClick={() => setManualMode(!manualMode)}
            className="text-[11px] text-zinc-500 hover:text-zinc-800 underline"
            data-testid="settlement-manual-mode-toggle"
          >
            {manualMode ? t("settlements.items_mode_link") : t("settlements.manual_mode_link")}
          </button>
        )}
        {distId && pricing.length === 0 && !isMaster && (
          <p className="text-[11px] text-zinc-500" data-testid="settlement-no-pricing-hint">
            {t("settlements.no_pricing_fallback")}
          </p>
        )}

        {distId && !usingItems && (
          <>
            <div>
              <label className="block text-xs font-semibold mb-1">{t("settlements.wholesale_basis")}</label>
              <BrutalInput type="number" step="0.01" min="0" value={manualBasis}
                onChange={(e) => setManualBasis(e.target.value)}
                placeholder={t("settlements.wholesale_placeholder")} data-testid="settlement-assign-wholesale-input" />
              <p className="text-[11px] text-zinc-500 mt-1">{t("settlements.wholesale_hint")}</p>
            </div>
            <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 text-sm flex items-baseline justify-between">
              <span className="text-xs text-zinc-600">{t("settlements.computed_credit")}</span>
              <span className={`text-base font-semibold tabular-nums ${manualCredit == null ? "text-zinc-400" : manualCredit >= 0 ? "text-emerald-700" : "text-rose-700"}`}
                data-testid="settlement-computed-credit">
                {manualCredit == null ? "—" : fmt$(manualCredit)}
              </span>
            </div>
          </>
        )}

        <div>
          <label className="block text-xs font-semibold mb-1">{t("settlements.note")}</label>
          <textarea rows={2} className="input-brutal w-full" value={note} onChange={(e) => setNote(e.target.value)}
            data-testid="settlement-assign-note-input" />
        </div>
        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <BrutalButton type="button" variant="secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </BrutalButton>
          {usingItems ? (
            <>
              <BrutalButton type="button" variant="secondary" className="flex-1"
                disabled={busy || validItems.length === 0}
                onClick={() => submit(false)} data-testid="settlement-assign-open-btn">
                {busy ? t("common.saving") : t("settlements.assign_keep_open")}
              </BrutalButton>
              <BrutalButton type="button" className="flex-1"
                disabled={busy || validItems.length === 0}
                onClick={() => submit(true)} data-testid="settlement-finalize-btn">
                <CheckCircle size={14} weight="bold" /> {busy ? t("common.saving") : t("settlements.finalize_payment")}
              </BrutalButton>
            </>
          ) : (
            <BrutalButton type="button" className="flex-1" disabled={busy || !distId}
              onClick={() => submit(false)} data-testid="settlement-assign-submit">
              {busy ? t("common.saving") : t("settlements.confirm_assign")}
            </BrutalButton>
          )}
        </div>
      </div>
    </div>
  );
}

function AddItemDialog({ settlement, onClose, onAdded, t }) {
  const [pricing, setPricing] = useState([]);
  const [productId, setProductId] = useState("");
  const [qty, setQty] = useState(1);
  const [unitBasis, setUnitBasis] = useState("");
  const [busy, setBusy] = useState(false);

  const fmt$ = (n) => `$${Number(n || 0).toFixed(2)}`;

  useEffect(() => {
    const req = settlement.master_sale
      ? api.get("/products").then(({ data }) =>
          (data || [])
            .filter((p) => (p.wholesale_price ?? p.suggested_retail_price) != null)
            .map((p) => ({
              product_id: p.id,
              name: p.name,
              their_wholesale: p.wholesale_price ?? p.suggested_retail_price,
              wholesale_price: p.wholesale_price,
              suggested_retail_price: p.suggested_retail_price,
              current_stock: p.current_stock,
            })))
      : api.get(`/distributors/${settlement.assigned_distributor_id}/pricing`).then(({ data }) =>
          (data || []).filter((r) => (r.their_wholesale ?? r.base_wholesale) != null));
    req.then(setPricing).catch(() => {});
  }, [settlement.assigned_distributor_id, settlement.master_sale]);

  const line = +((Number(unitBasis) || 0) * Math.max(1, Number(qty) || 1)).toFixed(2);
  const after = +(Number(settlement.remaining_balance || 0) - line).toFixed(2);

  const submit = async (e) => {
    e.preventDefault();
    if (!productId) {
      toast.error(t("settlements.form_invalid"));
      return;
    }
    setBusy(true);
    try {
      await api.post(`/settlements/${settlement.id}/add-item`, {
        product_id: productId,
        quantity: Math.max(1, Number(qty) || 1),
        unit_basis: unitBasis === "" ? null : Number(unitBasis),
      });
      toast.success(t("settlements.item_added"));
      onAdded();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="settlement-add-item-dialog"
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md space-y-3">
        <h2 className="text-xl font-semibold tracking-tight">{t("settlements.add_item_title")}</h2>
        <p className="text-sm text-zinc-600">
          {settlement.master_sale ? t("settlements.master_label") : settlement.assigned_distributor_name} · {t("settlements.remaining")}: <span className="font-semibold">{fmt$(settlement.remaining_balance)}</span>
        </p>
        <div>
          <label className="block text-xs font-semibold mb-1">{t("settlements.pick_product")}</label>
          <select
            className="input-brutal w-full"
            value={productId}
            onChange={(e) => {
              const pid = e.target.value;
              setProductId(pid);
              const r = pricing.find((x) => x.product_id === pid);
              const u = r ? Number(r.their_wholesale ?? r.base_wholesale) : null;
              setUnitBasis(u != null ? u.toFixed(2) : "");
            }}
            data-testid="settlement-add-item-product"
            required
          >
            <option value="">— {t("settlements.pick_product")} —</option>
            {pricing.map((r) => (
              <option key={r.product_id} value={r.product_id}>
                {r.name} · ${Number(r.their_wholesale ?? r.base_wholesale).toFixed(2)} · {t("settlements.held_n", { n: r.current_stock })}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold mb-1">{t("settlements.qty")}</label>
            <BrutalInput type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)}
              data-testid="settlement-add-item-qty" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">{t("settlements.unit_basis")}</label>
            <BrutalInput type="number" step="0.01" min="0" value={unitBasis}
              onChange={(e) => setUnitBasis(e.target.value)} data-testid="settlement-add-item-basis" />
            {(() => {
              const r = pricing.find((x) => x.product_id === productId);
              return r ? (
                <PriceChips
                  wholesale={r.wholesale_price ?? r.their_wholesale ?? r.base_wholesale}
                  retail={r.suggested_retail_price}
                  value={unitBasis}
                  onPick={(v) => setUnitBasis(v)}
                  testIdPrefix="settlement-add-item-chip"
                />
              ) : null;
            })()}
          </div>
        </div>
        <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 text-sm flex items-baseline justify-between">
          <span className="text-xs text-zinc-600">{t("settlements.remaining_after")}</span>
          <span
            className={`text-base font-semibold tabular-nums ${after === 0 ? "text-emerald-700" : after > 0 ? "text-amber-700" : "text-rose-700"}`}
            data-testid="settlement-add-item-remaining-after"
          >
            {fmt$(after)}
          </span>
        </div>
        <div className="flex gap-3 pt-2">
          <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </BrutalButton>
          <BrutalButton type="submit" className="flex-1" disabled={busy || !productId} data-testid="settlement-add-item-submit">
            {busy ? t("common.saving") : t("settlements.add_item")}
          </BrutalButton>
        </div>
      </form>
    </div>
  );
}


function PayoutDialog({ settlement, onClose, onPaid, t }) {
  const gross = Number(settlement.distributor_credit || 0);
  const [charges, setCharges] = useState(null); // null = loading
  const [checked, setChecked] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const fmt$ = (n) => `$${Number(n || 0).toFixed(2)}`;

  useEffect(() => {
    api
      .get(`/distributors/${settlement.assigned_distributor_id}/charges`)
      .then(({ data }) => {
        const outstanding = (data.items || []).filter((c) => c.status === "outstanding");
        setCharges(outstanding);
        const initial = {};
        outstanding.forEach((c) => { initial[c.id] = true; });
        setChecked(initial);
      })
      .catch(() => setCharges([]));
  }, [settlement.assigned_distributor_id]);

  const appliedIds = Object.keys(checked).filter((id) => checked[id]);
  const deductionsTotal = +(charges || [])
    .filter((c) => checked[c.id])
    .reduce((s, c) => s + Number(c.amount), 0)
    .toFixed(2);
  const net = +(gross - deductionsTotal).toFixed(2);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/settlements/${settlement.id}/mark-paid-out`, {
        note: note.trim() || null,
        apply_charge_ids: appliedIds.length > 0 ? appliedIds : null,
      });
      toast.success(t("settlements.paid_out_success"));
      onPaid();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="settlement-payout-dialog"
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md space-y-3">
        <h2 className="text-xl font-semibold tracking-tight">
          {t("settlements.payout_dialog_title", { name: settlement.assigned_distributor_name })}
        </h2>

        <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 text-sm space-y-1.5">
          <div className="flex justify-between">
            <span className="text-xs text-zinc-600">{t("settlements.payout_gross")}</span>
            <span className="tabular-nums font-medium" data-testid="payout-gross">{fmt$(gross)}</span>
          </div>
          {charges === null ? (
            <div className="text-xs text-zinc-400">{t("common.loading")}</div>
          ) : charges.length === 0 ? (
            <div className="text-xs text-zinc-500" data-testid="payout-no-deductions">
              {t("settlements.payout_no_deductions")}
            </div>
          ) : (
            <div className="space-y-1 border-t border-zinc-200 pt-1.5">
              <div className="text-xs font-semibold text-zinc-700">{t("settlements.payout_deductions")}</div>
              {charges.map((c) => (
                <label key={c.id} className="flex items-start gap-2 cursor-pointer select-none"
                  data-testid={`payout-charge-${c.id}`}>
                  <input type="checkbox" checked={!!checked[c.id]}
                    onChange={(e) => setChecked((prev) => ({ ...prev, [c.id]: e.target.checked }))}
                    className="mt-0.5 accent-rose-700"
                    data-testid={`payout-charge-check-${c.id}`} />
                  <span className="text-xs leading-snug flex-1">
                    <span className="font-semibold text-rose-700 tabular-nums">−{fmt$(c.amount)}</span>{" "}
                    <span className="text-zinc-700">{c.reason}</span>
                  </span>
                </label>
              ))}
            </div>
          )}
          <div className="flex justify-between border-t border-zinc-200 pt-1.5">
            <span className="text-xs font-semibold text-zinc-700">{t("settlements.payout_net")}</span>
            <span className={`text-base font-semibold tabular-nums ${net >= 0 ? "text-emerald-700" : "text-rose-700"}`}
              data-testid="payout-net">
              {fmt$(net)}
            </span>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold mb-1">{t("settlements.note")}</label>
          <textarea rows={2} className="input-brutal w-full" value={note}
            onChange={(e) => setNote(e.target.value)} data-testid="payout-note-input" />
        </div>
        <div className="flex gap-3 pt-2">
          <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </BrutalButton>
          <BrutalButton type="submit" className="flex-1" disabled={busy || charges === null}
            data-testid="payout-confirm-btn">
            <CheckCircle size={14} weight="bold" /> {busy ? t("common.saving") : t("settlements.payout_confirm_btn")}
          </BrutalButton>
        </div>
      </form>
    </div>
  );
}

